import {
  SmartRateCellVariant,
  smartRateCellVariantFromJSON,
} from '@classpass/protobuf-definitions/lib/web/models/partners/class_utilization_insights';

import { HEATMAP_DAYS } from 'constants/heatmap';
import { formatHeatmapHourLabel, getUtilizationHeatmapColor } from 'helpers/heatmap';

export { HEATMAP_DAYS as DAYS };

export const sortClassesByName = (classes = []) =>
  [...classes].sort((a, b) => (a.className || '').localeCompare(b.className || ''));

export const CHART_METRIC = Object.freeze({
  TOTAL_EARNINGS: 'TOTAL_EARNINGS',
  AVERAGE_PAYOUT_RATE: 'AVERAGE_PAYOUT_RATE',
});

export const VENUE_NOT_ON_SMART_RATE_MESSAGE = 'Venue is not on SmartRate';

export const CELL_STATE = Object.freeze({
  EARNINGS: 'EARNINGS',
  NO_SPOTS: 'NO_SPOTS',
  NO_CP_BOOKINGS: 'NO_CP_BOOKINGS',
  EMPTY: 'EMPTY',
});

const HEATMAP_LIGHT_TEXT_MIN_PERCENT = 70;
const HEATMAP_MIN_COLOR_PERCENT = 5;
const HEATMAP_SINGLE_PEER_PERCENT = 50;

export const collectEarningsPeerValues = rows => {
  if (!rows) return [];

  return rows.flatMap(row =>
    row.cells
      .filter(cell => cell.state === CELL_STATE.EARNINGS)
      .map(cell => Number(cell.value))
      .filter(Number.isFinite)
  );
};

export const getEarningsPercentile = (value, peerValues = []) => {
  const numeric = Number(value);
  const peers = peerValues.map(Number).filter(Number.isFinite);

  if (!Number.isFinite(numeric) || peers.length === 0) {
    return 0;
  }

  if (peers.length === 1) {
    return HEATMAP_SINGLE_PEER_PERCENT;
  }

  const lessCount = peers.filter(peer => peer < numeric).length;
  const equalCount = peers.filter(peer => peer === numeric).length;

  return ((lessCount + (equalCount - 1) / 2) / (peers.length - 1)) * 100;
};

export const getEarningsColor = (value, peerValues = []) => {
  const percentile = getEarningsPercentile(value, peerValues);

  return {
    background: getUtilizationHeatmapColor(Math.max(HEATMAP_MIN_COLOR_PERCENT, percentile)),
    text: percentile >= HEATMAP_LIGHT_TEXT_MIN_PERCENT ? 'light' : 'dark',
  };
};

export const buildEarningsFormatter = (currencyCode = 'USD', { compact = false, decimal = false } = {}) => {
  const fullFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: decimal ? 2 : 0,
    minimumFractionDigits: decimal ? 2 : 0,
  });
  if (!compact) return fullFormatter;
  const compactFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 1,
    notation: 'compact',
  });
  return {
    format: value => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return fullFormatter.format(0);
      if (numeric <= 999) return fullFormatter.format(numeric);
      return compactFormatter.format(numeric);
    },
  };
};

const buildPayoutRateMobileFormatter = (currencyCode = 'USD') => {
  const decimalFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const roundedFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const compactFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 1,
    notation: 'compact',
  });
  return {
    format: value => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return decimalFormatter.format(0);
      if (numeric < 100) return decimalFormatter.format(numeric);
      if (numeric <= 999) return roundedFormatter.format(numeric);
      return compactFormatter.format(numeric);
    },
  };
};

const KNOWN_OVERRIDES = new Set(['SGD']);

const withMobileCurrencySymbol = (formatter, currencyCode) => ({
  format: value => {
    const [, sign, symbol, rest] = formatter.format(value).match(/^(-?)(\D+)(.*)/);
    if (symbol.endsWith('$') || KNOWN_OVERRIDES.has(currencyCode)) return `${sign}$${rest}`;
    if (symbol.length === 1) return `${sign}${symbol}${rest}`;
    return `${sign}${rest}`;
  },
});

export const CELL_FORMATTERS_BY_METRIC = {
  [CHART_METRIC.TOTAL_EARNINGS]: currencyCode => ({
    full: buildEarningsFormatter(currencyCode),
    compact: withMobileCurrencySymbol(buildEarningsFormatter(currencyCode, { compact: true }), currencyCode),
  }),
  [CHART_METRIC.AVERAGE_PAYOUT_RATE]: currencyCode => ({
    full: buildEarningsFormatter(currencyCode, { decimal: true }),
    compact: withMobileCurrencySymbol(buildPayoutRateMobileFormatter(currencyCode), currencyCode),
  }),
};

const orderedRowsByHour = rowsByHour =>
  Array.from(rowsByHour.values()).sort((a, b) => {
    const aHour = Number(a.hour);
    const bHour = Number(b.hour);
    if (Number.isFinite(aHour) && Number.isFinite(bHour)) return aHour - bHour;
    return a.time.localeCompare(b.time);
  });

const getCellForDatapoint = datapoint => {
  const base = {
    classpassBooked: datapoint.classpass_booked ?? null,
    excessSpotsFilledPercent: datapoint.excess_spots_filled_percent ?? null,
    day: datapoint.day ?? null,
    time: datapoint.time ?? null,
  };
  switch (smartRateCellVariantFromJSON(datapoint.smartrate_cell_variant)) {
    case SmartRateCellVariant.HAS_DATA:
      return { ...base, state: CELL_STATE.EARNINGS, value: datapoint.value };
    case SmartRateCellVariant.NO_CP_SPOTS:
      return { ...base, state: CELL_STATE.NO_SPOTS };
    case SmartRateCellVariant.NO_CP_BOOKINGS:
      return { ...base, state: CELL_STATE.NO_CP_BOOKINGS };
    case SmartRateCellVariant.NO_SCHEDULES:
    default:
      return { state: CELL_STATE.EMPTY };
  }
};

export const getClassStorageKey = venueId => `weekly-earnings-class-${venueId}`;

export const readPersistedClassId = venueId => {
  try {
    return sessionStorage.getItem(getClassStorageKey(venueId)) || null;
  } catch {
    return null;
  }
};

export const persistClassId = (venueId, classId) => {
  try {
    if (classId != null) {
      sessionStorage.setItem(getClassStorageKey(venueId), String(classId));
    } else {
      sessionStorage.removeItem(getClassStorageKey(venueId));
    }
  } catch {
    // sessionStorage unavailable
  }
};

export const isVenueNotOnSmartRateError = error =>
  error?.response?.status === 400 &&
  error?.response?.data?.code === 400 &&
  error?.response?.data?.message === VENUE_NOT_ON_SMART_RATE_MESSAGE;

export const adaptWeeklyEarningsResponse = response => {
  const datapoints = (response?.data?.visualization?.heatmap?.data ?? [])
    .map(datapoint => datapoint.smartrate_datapoint)
    .filter(Boolean);

  const rowsByHour = new Map();
  datapoints.forEach(datapoint => {
    const hour = datapoint.column;
    if (!rowsByHour.has(hour)) {
      rowsByHour.set(hour, {
        hour,
        time: formatHeatmapHourLabel(datapoint.time),
        cells: HEATMAP_DAYS.map(() => ({ state: CELL_STATE.EMPTY })),
      });
    }

    // `row` is the day-of-week index (0 = Sunday); proto3 omits the default 0.
    const dayIndex = datapoint.row ?? 0;
    if (dayIndex >= 0 && dayIndex < HEATMAP_DAYS.length) {
      rowsByHour.get(hour).cells[dayIndex] = getCellForDatapoint(datapoint);
    }
  });

  return {
    currency: response?.data?.currency || 'USD',
    hasMoreThan8WeeksData: response?.has_more_than_8_weeks_data !== false,
    hasQualifyingReservationCount: response?.has_qualifying_reservation_count !== false,
    lastUpdatedOn: response?.last_updated_on,
    mustSelectClassType: !!response?.must_select_class_type,
    rows: orderedRowsByHour(rowsByHour),
  };
};

export const createComingSoonWeeklyEarningsData = () =>
  adaptWeeklyEarningsResponse({
    has_more_than_8_weeks_data: false,
    has_qualifying_reservation_count: true,
    must_select_class_type: false,
  });
